import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HardHatIcon, PackageIcon, TruckIcon, FactoryIcon, ArrowRightIcon, StoreIcon, HeartPulseIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { useTranslation } from '../../lib/i18n';
export function Applications() {
  const {
    lang
  } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const applications = lang === 'en' ? [{
    icon: PackageIcon,
    title: 'Logistics & Delivery'
  }, {
    icon: HardHatIcon,
    title: 'Construction Materials'
  }, {
    icon: TruckIcon,
    title: 'Moving & Furniture'
  }, {
    icon: FactoryIcon,
    title: 'Industrial Warehouses'
  }, {
    icon: StoreIcon,
    title: 'Retail & HoReCa'
  }, {
    icon: HeartPulseIcon,
    title: 'Healthcare Facilities'
  }] : lang === 'ru' ? [{
    icon: PackageIcon,
    title: 'Логистика и доставка'
  }, {
    icon: HardHatIcon,
    title: 'Строительные материалы'
  }, {
    icon: TruckIcon,
    title: 'Переезды и мебель'
  }, {
    icon: FactoryIcon,
    title: 'Промышленные склады'
  }, {
    icon: StoreIcon,
    title: 'Розничная торговля и HoReCa'
  }, {
    icon: HeartPulseIcon,
    title: 'Медицинские учреждения'
  }] : [{
    icon: PackageIcon,
    title: 'Logistika i dostava'
  }, {
    icon: HardHatIcon,
    title: 'Građevinski materijal'
  }, {
    icon: TruckIcon,
    title: 'Selidbe i nameštaj'
  }, {
    icon: FactoryIcon,
    title: 'Industrijska skladišta'
  }, {
    icon: StoreIcon,
    title: 'Maloprodaja i HoReCa'
  }, {
    icon: HeartPulseIcon,
    title: 'Zdravstvene ustanove'
  }];
  const INTERVAL = 3500;
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      setActiveIndex(current => (current + 1) % applications.length);
    }, INTERVAL);
    return () => clearInterval(timer);
  }, [isHovered, applications.length]);
  const activeApp = applications[activeIndex];
  const ActiveIcon = activeApp.icon;
  return <section className="py-16 md:py-24 bg-dark-bg overflow-hidden" data-id="element-121">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-122">
        <div className="text-center mb-10 md:mb-14" data-id="element-123">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight" data-id="element-124">
            <span className="text-brand-yellow" data-id="element-125">
              {lang === 'en' ? 'Where is it used?' : lang === 'ru' ? 'Где используется?' : 'Gde se koristi?'}
            </span>
          </h2>
        </div>

        <div className="relative bg-dark-surface border border-dark-border rounded-2xl p-6 md:p-10 shadow-2xl" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onTouchStart={() => setIsHovered(true)} onTouchEnd={() => setIsHovered(false)} data-id="element-126">
          {/* Spotlight Area */}
          <div className="relative h-56 md:h-64 flex items-center justify-center mb-8" data-id="element-127">
            <AnimatePresence mode="wait" data-id="element-128">
              <motion.div key={activeIndex} initial={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -10,
              scale: 0.95
            }} transition={{
              duration: 0.3,
              ease: 'easeOut'
            }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-2" data-id="element-129">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-brand-yellow/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(253,185,19,0.15)] flex-shrink-0" data-id="element-130">
                  <ActiveIcon className="w-10 h-10 md:w-12 md:h-12 text-brand-yellow" data-id="element-131" />
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight leading-tight" data-id="element-132">
                  {activeApp.title}
                </h3>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-1 bg-dark-border rounded-full mb-8 overflow-hidden" data-id="element-133">
            <motion.div key={activeIndex + (isHovered ? '-hover' : '')} initial={{
            width: isHovered ? '100%' : '0%'
          }} animate={{
            width: '100%'
          }} transition={{
            duration: isHovered ? 0 : INTERVAL / 1000,
            ease: 'linear'
          }} className="h-full bg-brand-yellow" data-id="element-134" />
          </div>

          {/* Chips */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3" data-id="element-135">
            {applications.map((app, idx) => {
            const Icon = app.icon;
            const isActive = idx === activeIndex;
            return <button key={app.title} onClick={() => setActiveIndex(idx)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 border ${isActive ? 'bg-brand-yellow text-dark-bg border-brand-yellow shadow-[0_0_15px_rgba(253,185,19,0.3)]' : 'bg-dark-bg text-gray-400 border-dark-border hover:border-gray-500 hover:text-white'}`} data-id="element-136">
                  <Icon className={`w-3.5 h-3.5 md:w-4 md:h-4 ${isActive ? 'text-dark-bg' : 'text-gray-500'}`} data-id="element-137" />
                  {app.title}
                </button>;
          })}
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-138">
          <Link to="/applications" data-id="element-139">
            <Button variant="secondary" size="lg" className="group" data-id="element-140">
              {lang === 'en' ? 'View all applications' : lang === 'ru' ? 'Посмотреть все применения' : 'Pogledajte sve primene'}
              <ArrowRightIcon className="w-5 h-5 ml-2 inline transition-transform group-hover:translate-x-1" data-id="element-141" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}